/**
 * The two rates a venue is priced by — #2630.
 *
 * ## Why this exists
 *
 * A venue is priced from exactly two price rows: the one whose `days` is the
 * literal `'weekday'` and the one whose `days` is the literal `'weekend'`.
 * That is not a convention this file invents — it is what the server charges
 * by:
 *
 *  - `new-backend/admin/src/lib/pricing/computePricing.ts` (~line 100) reads
 *    `pricescales.find((s) => s.days === 'weekday')` and the `'weekend'`
 *    twin, and prices the stay from those two alone.
 *  - `new-backend/admin/src/lib/pricing/coverage.ts` (~line 195) raises
 *    `missing_weekday_rate` / `missing_weekend_rate` when either is absent,
 *    which is the warning the admin prints on the price card.
 *  - `frontend/admin-ui/src/pages/assets/AssetCreatePage.tsx` seeds exactly
 *    those two rows when an operator creates a venue.
 *
 * These are separate deployables and cannot import each other, so the copies
 * name each other and are pinned by a test (ADR-0014). **If the venue rate
 * lookup changes in `computePricing.ts`, it changes here in the same PR.**
 *
 * ## The bug this closes
 *
 * `BookingSummary` used to read the same two rows and then fall back:
 *
 *     priceScale.find((p) => p.days === 'weekday')?.price ?? pricePerNight
 *
 * `pricePerNight` is the list card's "from" figure — a plain minimum over all
 * price rows, which is NOT what the server charges by. So for any venue whose
 * rows the venue model cannot read, the customer was quoted a number the
 * invoice would never carry. Measured on integration (#2630 C3): booking
 * `D-00000016` was shown **120,00 €** and priced by the admin at **50,00 €**,
 * because `QA Saal A` carries a single `days = 'mon,tue,wed,thu,fri,sat,sun'`
 * row that the venue model reads as "no rate configured".
 *
 * A missing rate is now returned as `null` and rendered as such. Absent is
 * absent: inventing a rate here is what produced a quote nobody could honour.
 */

export interface VenuePriceRow {
    days?: string | null
    /** Serialized Prisma `Decimal` reaches the client as a string. */
    price?: number | string | null
}

export interface VenueRates {
    /** €/day for Mon–Fri, or `null` when no weekday rate is configured. */
    weekday: number | null
    /** €/day for Sat–Sun, or `null` when no weekend rate is configured. */
    weekend: number | null
}

const rateFor = (
    rows: readonly VenuePriceRow[],
    label: 'weekday' | 'weekend',
): number | null => {
    const row = rows.find((r) => r?.days === label)
    if (!row || row.price === null || row.price === undefined) return null
    const price = Number(row.price)
    // A row whose price does not parse is not a rate. Zero is: a venue may
    // genuinely be free, and the server prices it that way too.
    return Number.isFinite(price) ? price : null
}

export const resolveVenueRates = (
    rows: readonly VenuePriceRow[] | null | undefined,
): VenueRates => {
    const safe = Array.isArray(rows) ? rows : []
    return {
        weekday: rateFor(safe, 'weekday'),
        weekend: rateFor(safe, 'weekend'),
    }
}
